import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import type { MetricValues } from '../../components/PageConfigProvider/types/metric';
import { FilterId } from '../../filters/types/filters';

const geographySentimentMetrics: MetricValues[] = [
  'overall',
  'business_outlook',
  'career_growth',
  'compensation',
  'benefits',
  'healthcare',
  'retirement_pension',
  'childcare',
  'education_assistance',
  'wellness_programs',
  'work_life_balance',
  'diversity_and_inclusion',
  'leadership',
  'culture',
  'remote_work',
  'onboarding',
  'mentorship',
  'technology',
  'promotion_opportunities',
  'paid_time_off',
  'transparency',
  'efficiency_and_bureaucracy',
  'product_and_service_quality',
  'prestige',
  'coworkers',
  'mission_and_vision',
  'workplace_facilities',
  'training',
  'clients_and_customers',
  'company_growth',
  'schedule_flexibility',
  'job_security',
  'overtime',
  'family_support',
  'commission',
  'independence',
  'commute',
  'safety',
  'internal_communication',
  'office_politics',
  'harassment',
  'honesty',
  'ethics',
  'commitment',
  'compliance',
  'integrity',
];

const sentimentInfoText =
  'Sentiment scores are derived from both the free text and the star ratings associated with employee reviews, and normalized so that 50% is the median score amongst all geographies. Sample size includes all reviews with star ratings predictive of a specific sentiment topic, as well as all reviews that contain any signal for that sentiment topic in their free text, within the previous 12 months.';

const sentimentBarAxisRange = {
  maxValue: 1,
  minValue: 0,
};

export const geographySentimentSnapshotItems: TabContent['items'] = [
  {
    position: { x: 0, width: 11, y: 0, height: 12 },
    id: 'geography-sentiment-snapshot-overall',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: geographySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 11, width: 11, y: 0, height: 12 },
    id: 'geography-sentiment-snapshot-business-outlook',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: geographySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 22, width: 11, y: 0, height: 12 },
    id: 'geography-sentiment-snapshot-career-growth',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: geographySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 0, width: 11, y: 12, height: 12 },
    id: 'geography-sentiment-snapshot-compensation',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: geographySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 11, width: 11, y: 12, height: 12 },
    id: 'geography-sentiment-snapshot-benefits',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: geographySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 22, width: 11, y: 12, height: 12 },
    id: 'geography-sentiment-snapshot-culture',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: geographySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 0, width: 11, y: 24, height: 12 },
    id: 'geography-sentiment-snapshot-work-life-balance',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: geographySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 11, width: 11, y: 24, height: 12 },
    id: 'geography-sentiment-snapshot-diversity-and-inclusion',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: geographySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 22, width: 11, y: 24, height: 12 },
    id: 'geography-sentiment-snapshot-leadership',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: geographySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 33, width: 15, y: 0, height: 18 },
    id: 'geography-sentiment-snapshot-positive-reviews',
    title: 'Positive Reviews',
    info: {
      text: 'An AI-generated summary of all positive reviews.',
    },
    plot: {
      defaultMetricOptions: ['positive_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
  {
    position: { x: 33, width: 15, y: 18, height: 18 },
    id: 'geography-sentiment-snapshot-negative-reviews',
    title: 'Negative Reviews',
    info: {
      text: 'An AI-generated summary of all negative reviews.',
    },
    plot: {
      defaultMetricOptions: ['negative_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
];

export const geographySentimentSingleSnapshotItems: TabContent['items'] = [
  {
    position: { x: 0, width: 33, y: 0, height: 36 },
    id: 'geography-sentiment-topics',
    title: 'Sentiment Topics',
    info: {
      text: 'Sentiment scores are derived from both the free text and the star ratings associated with employee reviews, and normalized so that 50% is the median score amongst all geographies. Sample size includes all reviews with star ratings predictive of a specific sentiment topic, as well as all reviews that contain any signal for that sentiment topic in their free text, within the previous 12 months.',
    },
    plot: {
      defaultMetricOptions: ['sentiment_top_bottom'],
      chartType: 'diverging-bar',
      divergingBar: {
        negativeLabel: 'Factors of Attrition',
        positiveLabel: 'Factors of Hiring',
      },
    },
  },
  {
    position: { x: 33, width: 15, y: 0, height: 18 },
    id: 'geography-sentiment-snapshot-positive-reviews',
    title: 'Positive Reviews',
    info: {
      text: 'An AI-generated summary of all positive reviews.',
    },
    plot: {
      defaultMetricOptions: ['positive_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
  {
    position: { x: 33, width: 15, y: 18, height: 18 },
    id: 'geography-sentiment-snapshot-negative-reviews',
    title: 'Negative Reviews',
    info: {
      text: 'An AI-generated summary of all negative reviews.',
    },
    plot: {
      defaultMetricOptions: ['negative_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
];

const geographySentimentOvertime: TabContent['items'] = [
  {
    position: { x: 0, width: 11, y: 0, height: 12 },
    id: 'geography-sentiment-overtime-overall',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: geographySentimentMetrics,
    },
  },
  {
    position: { x: 11, width: 11, y: 0, height: 12 },
    id: 'geography-sentiment-overtime-business-outlook',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: geographySentimentMetrics,
    },
  },
  {
    position: { x: 22, width: 11, y: 0, height: 12 },
    id: 'geography-sentiment-overtime-career-growth',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: geographySentimentMetrics,
    },
  },
  {
    position: { x: 0, width: 11, y: 12, height: 12 },
    id: 'geography-sentiment-overtime-compensation',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: geographySentimentMetrics,
    },
  },
  {
    position: { x: 11, width: 11, y: 12, height: 12 },
    id: 'geography-sentiment-overtime-benefits',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: geographySentimentMetrics,
    },
  },
  {
    position: { x: 22, width: 11, y: 12, height: 12 },
    id: 'geography-sentiment-overtime-culture',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: geographySentimentMetrics,
    },
  },
  {
    position: { x: 0, width: 11, y: 24, height: 12 },
    id: 'geography-sentiment-overtime-work-life-balance',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: geographySentimentMetrics,
    },
  },
  {
    position: { x: 11, width: 11, y: 24, height: 12 },
    id: 'geography-sentiment-overtime-diversity-and-inclusion',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: geographySentimentMetrics,
    },
  },
  {
    position: { x: 22, width: 11, y: 24, height: 12 },
    id: 'geography-sentiment-overtime-leadership',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: geographySentimentMetrics,
    },
  },
  {
    position: { x: 33, width: 15, y: 0, height: 18 },
    id: 'geography-sentiment-overtime-positive-reviews',
    title: 'Trending Positive Reviews',
    info: {
      text: 'An AI-generated summary of all positive reviews.',
    },
    plot: {
      defaultMetricOptions: ['positive_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
  {
    position: { x: 33, width: 15, y: 18, height: 18 },
    id: 'geography-sentiment-overtime-negative-reviews',
    title: 'Trending Negative Reviews',
    info: {
      text: 'An AI-generated summary of all negative reviews.',
    },
    plot: {
      defaultMetricOptions: ['negative_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
];

const sharedConfig: Required<Pick<TabContent, 'primaryFilter' | 'grid'>> = {
  primaryFilter: {
    id: FilterId.GEOGRAPHY,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
};

const geographySentimentSnapshotTab: TabContent = {
  id: 'geography-sentiment-snapshot',
  items: geographySentimentSingleSnapshotItems,
  ...sharedConfig,
  secondaryFilters: [
    FilterId.OVERALL_RATING,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.COMPANY,
    FilterId.INDUSTRY,
    FilterId.SNAPSHOT_DATE,
  ].map((id) => ({ id })),
};

const geographySentimentOvertimeTab: TabContent = {
  id: 'geography-sentiment-overtime',
  items: geographySentimentOvertime,
  ...sharedConfig,
  primaryFilter: { ...sharedConfig.primaryFilter, tagColors: true },
  secondaryFilters: [
    FilterId.OVERALL_RATING,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.COMPANY,
    FilterId.INDUSTRY,
    FilterId.DATE_RANGE,
  ].map((id) => ({ id })),
};

export const geographySentiment: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.GEOGRAPHY_SENTIMENT,
  revelioPageId: RevelioPageId.GEOGRAPHY_SENTIMENT,
  name: 'Sentiment',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'geography-sentiment-snapshot',
      label: 'Snapshot',
      tabContent: geographySentimentSnapshotTab,
      isActive: true,
    },
    {
      id: 'geography-sentiment-overtime',
      label: 'Over Time',
      tabContent: geographySentimentOvertimeTab,
      isActive: false,
    },
  ],
};

export const geographySentimentDefaultMetric: Record<string, MetricValues> = {
  'geography-sentiment-snapshot-overall': 'overall',
  'geography-sentiment-snapshot-business-outlook': 'business_outlook',
  'geography-sentiment-snapshot-career-growth': 'career_growth',
  'geography-sentiment-snapshot-compensation': 'compensation',
  'geography-sentiment-snapshot-benefits': 'benefits',
  'geography-sentiment-snapshot-culture': 'culture',
  'geography-sentiment-snapshot-work-life-balance': 'work_life_balance',
  'geography-sentiment-snapshot-diversity-and-inclusion':
    'diversity_and_inclusion',
  'geography-sentiment-snapshot-leadership': 'leadership',
  'geography-sentiment-overtime-overall': 'overall',
  'geography-sentiment-overtime-business-outlook': 'business_outlook',
  'geography-sentiment-overtime-career-growth': 'career_growth',
  'geography-sentiment-overtime-compensation': 'compensation',
  'geography-sentiment-overtime-benefits': 'benefits',
  'geography-sentiment-overtime-culture': 'culture',
  'geography-sentiment-overtime-work-life-balance': 'work_life_balance',
  'geography-sentiment-overtime-diversity-and-inclusion':
    'diversity_and_inclusion',
  'geography-sentiment-overtime-leadership': 'leadership',
};
